/* eslint-env browser */
// The signed-in portal shell (W2_Portal_Shell): a 56-px orange bar with the
// page name, search, language, notifications and who is signed in; a 220-px
// sidebar with the role's pinned pages and every other page in groups by
// job (a temporary drawer below md); per-page browser tab titles.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import QuickSearch from './QuickSearch';
import OutageBanner from './OutageBanner';
import AppUpdateNotice from './AppUpdateNotice';
import { renderLanguageItems } from './Header/LanguageMenu';
import MenuHeading from './Header/MenuHeading';
import { resetPageStates } from './modern/pageState';
import useLanguageSwitcher from './Header/useLanguageSwitcher';
import { NOTIFICATION_CAPABILITY } from '../common/notificationSecurity';
import { calcEst, showEst, optionsRequired } from '../common/sharedFunctions';
import { tokens as c, layout, iconHues } from '../theme/tokens';
// The round Donkey Drive 9260 badge the portal has always shown (200-px copy).
import brandLogo from './Header/logo-header-200.png';

const BAR = layout.APP_BAR_HEIGHT;
const SIDEBAR = layout.SIDEBAR_WIDTH;

const SEARCH_ROLES = ['admin', 'fleetadmin', 'finance', 'callcenteragent', 'callcentersupervisor'];
const BELL_ROLES = ['admin', 'finance', 'callcenteragent', 'callcentersupervisor'];
const WITHDRAW_ROLES = ['admin', 'finance', 'callcentersupervisor'];
const SOS_ROLES = ['admin', 'callcenteragent', 'callcentersupervisor'];
const COMPLAINT_ROLES = ['admin', 'callcentersupervisor'];

const ROLE_NAMES = {
  admin: 'Admin',
  fleetadmin: 'Fleet admin',
  finance: 'Finance',
  marketing: 'Marketing',
  scout: 'Scout',
  driver: 'Driver',
  customer: 'Customer',
  callcenteragent: 'Call-centre agent',
  callcentersupervisor: 'Call-centre supervisor',
  accountant: 'Accountant',
};

const under = (base) => (p) => p === base || p.startsWith(`${base}/`);
const exactly = (url) => (p) => p === url;

// Groups by job. Order here is the sidebar order; a group with no page for
// the signed-in role is skipped.
const MENU_GROUPS = [
  { key: 'overview', label: 'menu_group_overview', fallback: 'Overview' },
  { key: 'bookings', label: 'menu_group_bookings', fallback: 'Bookings & support' },
  { key: 'people_fleet', label: 'menu_group_people_fleet', fallback: 'People & fleet' },
  { key: 'finance', label: 'menu_group_finance', fallback: 'Money & reports' },
  { key: 'rewards', label: 'menu_group_rewards', fallback: 'Rewards & miles' },
  { key: 'admin', label: 'menu_group_admin', fallback: 'Administration' },
  { key: 'account', label: 'menu_group_account', fallback: 'Account' },
];

// Every portal page. `access` = roles that see it; `capability` = an
// additive per-user flag on /users/<uid> that also shows it; `setting` = a
// /settings switch that must be on.
const MENU_ITEMS = [
  // Overview
  { key: 'home', group: 'overview', name: 'home', fallback: 'Home', url: '/', Icon: HomeOutlinedIcon, hue: 'neutral', match: exactly('/'), access: ['admin', 'fleetadmin', 'finance', 'driver', 'customer', 'callcenteragent', 'callcentersupervisor', 'scout'] },
  { key: 'dashboard', group: 'overview', name: 'dashboard_text', fallback: 'Dashboard', url: '/dashboard', Icon: GridViewOutlinedIcon, hue: 'info', access: ['admin', 'fleetadmin', 'finance', 'callcenteragent', 'callcentersupervisor'] },
  // Bookings & support
  { key: 'bookings', group: 'bookings', name: 'nav_bookings', legacy: 'booking_history', fallback: 'Bookings', url: '/bookings', Icon: ListAltOutlinedIcon, hue: 'info', match: under('/bookings'), access: ['admin', 'fleetadmin', 'driver', 'customer', 'finance', 'callcenteragent', 'callcentersupervisor'] },
  { key: 'addbookings', group: 'bookings', name: 'nav_add_booking', legacy: 'addbookinglable', fallback: 'Add booking', url: '/addbookings', Icon: AddRoundedIcon, hue: 'accent', access: calcEst && !showEst && !optionsRequired ? ['customer'] : ['admin', 'fleetadmin', 'customer', 'finance', 'callcenteragent', 'callcentersupervisor'] },
  { key: 'sos', group: 'bookings', name: 'sos', fallback: 'SOS', url: '/sos', Icon: WarningAmberRoundedIcon, hue: 'danger', access: ['admin', 'callcenteragent', 'callcentersupervisor'] },
  { key: 'complain', group: 'bookings', name: 'nav_complaints', legacy: 'complain', fallback: 'Complaints', url: '/complain', Icon: ChatBubbleOutlineRoundedIcon, hue: 'amber', access: ['admin', 'callcenteragent', 'callcentersupervisor'] },
  // City events: entered by the call centre, published by supervisors; shown once settings.cityEventsEnabled is on.
  { key: 'cityevents', group: 'bookings', name: 'city_events_menu', fallback: 'City events', url: '/cityevents', Icon: ConfirmationNumberOutlinedIcon, hue: 'accent', access: ['admin', 'callcenteragent', 'callcentersupervisor'], setting: 'cityEventsEnabled' },
  // People & fleet
  { key: 'users', group: 'people_fleet', name: 'user', fallback: 'Users', url: '/users/1', Icon: PeopleAltOutlinedIcon, hue: 'success', match: under('/users'), access: ['admin', 'fleetadmin', 'finance', 'callcenteragent', 'callcentersupervisor'] },
  { key: 'onboard', group: 'people_fleet', name: 'onboard_driver', fallback: 'Onboard driver', url: '/onboard-driver', Icon: PersonAddAltOutlinedIcon, hue: 'success', access: ['admin', 'callcentersupervisor', 'callcenteragent', 'scout'] },
  { key: 'driverletter', group: 'people_fleet', name: 'driver_letter', fallback: 'Driver letter', url: '/driverletter', Icon: DescriptionOutlinedIcon, hue: 'neutral', access: ['admin', 'finance', 'callcentersupervisor', 'callcenteragent', 'fleetadmin'] },
  { key: 'agentperformance', group: 'people_fleet', name: 'agent_performance', fallback: 'Agent performance', url: '/agentperformance', Icon: AssessmentOutlinedIcon, hue: 'info', access: ['admin', 'callcentersupervisor', 'finance'] },
  { key: 'cartypes', group: 'people_fleet', name: 'nav_car_types', legacy: 'car_type', fallback: 'Car types', url: '/cartypes', Icon: CategoryOutlinedIcon, hue: 'neutral', match: under('/cartypes'), access: ['admin', 'finance'] },
  { key: 'cars', group: 'people_fleet', name: 'cars', fallback: 'Cars', url: '/cars', Icon: DirectionsCarOutlinedIcon, hue: 'neutral', match: under('/cars'), access: ['admin', 'fleetadmin', 'driver', 'finance', 'callcentersupervisor', 'callcenteragent'] },
  // Money & reports
  { key: 'withdraws', group: 'finance', name: 'withdraws_web', fallback: 'Withdraws', url: '/withdraws', Icon: PaymentsOutlinedIcon, hue: 'amber', access: ['admin', 'finance'] },
  { key: 'transactionlogs', group: 'finance', name: 'transaction_logs', fallback: 'Transaction logs', url: '/transactionlogs', Icon: ReceiptLongOutlinedIcon, hue: 'neutral', access: ['admin', 'finance'] },
  { key: 'addtowallet', group: 'finance', name: 'add_to_wallet', fallback: 'Add to wallet', url: '/addtowallet', Icon: AccountBalanceWalletOutlinedIcon, hue: 'success', access: ['admin', 'finance', 'callcentersupervisor'] },
  { key: 'userwallet', group: 'finance', name: 'my_wallet_tile', fallback: 'My wallet', url: '/userwallet', Icon: AccountBalanceWalletOutlinedIcon, hue: 'success', access: ['driver', 'customer', 'fleetadmin', 'finance'] },
  { key: 'eventpayments', group: 'finance', name: 'event_payments_menu', fallback: 'Event payments', url: '/eventpayments', Icon: LiveTvOutlinedIcon, hue: 'info', access: ['admin', 'finance', 'callcentersupervisor'] },
  { key: 'reports', group: 'finance', name: 'report', fallback: 'Reports', url: '/allreports', Icon: BarChartOutlinedIcon, hue: 'info', access: ['admin', 'fleetadmin', 'finance', 'callcentersupervisor', 'callcenteragent'] },
  // Rewards & miles
  { key: 'driverincentive', group: 'rewards', name: 'driver_incentive', fallback: 'Driver incentive', url: '/driverincentive', Icon: EmojiEventsOutlinedIcon, hue: 'amber', access: ['admin', 'finance', 'callcentersupervisor'] },
  { key: 'promos', group: 'rewards', name: 'promo', fallback: 'Promos', url: '/promos', Icon: LocalOfferOutlinedIcon, hue: 'accent', match: under('/promos'), access: ['admin', 'marketing'] },
  { key: 'phonecards', group: 'rewards', name: 'phone_cards_menu', fallback: 'Phone cards', url: '/phonecards', Icon: SimCardOutlinedIcon, hue: 'info', access: ['admin'] },
  { key: 'ngos', group: 'rewards', name: 'ngos_menu', fallback: 'NGOs', url: '/ngos', Icon: VolunteerActivismOutlinedIcon, hue: 'success', match: under('/ngos'), access: ['admin'] },
  { key: 'miledonations', group: 'rewards', name: 'mile_donations_menu', fallback: 'Mile donations', url: '/miledonations', Icon: VolunteerActivismOutlinedIcon, hue: 'success', access: ['admin', 'finance'] },
  // Administration
  { key: 'security', group: 'admin', name: 'security_dashboard', fallback: 'Security dashboard', url: '/security', Icon: SecurityOutlinedIcon, hue: 'neutral', access: ['admin'] },
  { key: 'releasenotes', group: 'admin', name: 'release_notes', fallback: 'Release notes', url: '/releasenotes', Icon: NewReleasesOutlinedIcon, hue: 'info', access: ['admin', 'finance', 'callcenteragent', 'callcentersupervisor'] },
  // Account
  { key: 'notifications', group: 'account', name: 'push_notifications', fallback: 'Push notifications', url: '/notifications', Icon: NotificationsActiveOutlinedIcon, hue: 'neutral', match: under('/notifications'), access: ['admin', 'marketing'], capability: NOTIFICATION_CAPABILITY },
  { key: 'settings', group: 'account', name: 'settings_title', fallback: 'Settings', url: '/settings', Icon: TuneRoundedIcon, hue: 'accent', access: ['admin'] },
  { key: 'paymentsettings', group: 'account', name: 'payment_settings', fallback: 'Payment settings', url: '/paymentsettings', Icon: CreditCardOutlinedIcon, hue: 'neutral', access: ['admin'] },
  { key: 'profile', group: 'account', name: 'profile', fallback: 'Profile', url: '/profile', Icon: PersonOutlineRoundedIcon, hue: 'neutral', access: ['admin', 'fleetadmin', 'driver', 'customer', 'finance', 'callcenteragent', 'callcentersupervisor', 'scout'] },
];

// Pages pinned above the groups for a role (the board's call-centre and
// admin sets). 'drivers' is the Users page opened on its Drivers tab.
const CALL_CENTRE_PINS = { label: 'nav_pinned_callcentre', fallback: 'Pinned for call centre', keys: ['addbookings', 'bookings', 'drivers', 'sos', 'complain'] };
const PIN_SETS = {
  callcenteragent: CALL_CENTRE_PINS,
  callcentersupervisor: CALL_CENTRE_PINS,
  admin: { label: 'nav_pinned_admin', fallback: 'Pinned for admin', keys: ['dashboard', 'bookings', 'drivers', 'withdraws'] },
};
const DRIVERS_PIN = {
  key: 'drivers',
  name: 'drivers',
  fallback: 'Drivers',
  url: '/users/1',
  Icon: PersonOutlineRoundedIcon,
  hue: 'success',
  match: (p) => p === '/users/1' || p.startsWith('/users/driverdetails/'),
  requires: 'users',
};

const USERS_TABS = {
  0: ['riders', 'Customers'],
  1: ['drivers', 'Drivers'],
  2: ['fleetadmins', 'Fleet admins'],
  3: ['internal_staff', 'Internal staff'],
  4: ['alladmin', 'All admins'],
};

const itemMatches = (item, pathname) => (item.match ? item.match(pathname) : pathname === item.url);

// A page name. Items whose wording changed carry their old live key as
// `legacy`: until the owner pushes the new key, each language keeps showing
// its old translation instead of falling back to English.
const labelOf = (t, item) => t(item.legacy ? [item.name, item.legacy] : item.name, item.fallback);
const LOG_OUT_KEYS = ['nav_log_out', 'logout'];

// Page name for the bar and the browser tab.
function resolvePageTitles(pathname, t, bookings) {
  const booking = pathname.match(/^\/bookings\/bookingdetails\/([^/]+)/);
  if (booking) {
    const id = decodeURIComponent(booking[1]);
    const found = Array.isArray(bookings) ? bookings.find((b) => b && (b.id === id || b.reference === id)) : null;
    const page = t('booking_details', 'Booking details');
    return {
      page,
      tab: found && found.reference ? t('page_booking_ref', { reference: found.reference, defaultValue: 'Booking {{reference}}' }) : page,
    };
  }
  const usersTab = pathname.match(/^\/users\/(\d)$/);
  if (usersTab && USERS_TABS[usersTab[1]]) {
    const page = t(USERS_TABS[usersTab[1]][0], USERS_TABS[usersTab[1]][1]);
    return { page, tab: page };
  }
  if (pathname.startsWith('/users/driverdetails/')) {
    const page = t('driver_details', 'Driver details');
    return { page, tab: page };
  }
  if (pathname.startsWith('/users/customerdetails/')) {
    const page = t('page_customer_details', 'Customer details');
    return { page, tab: page };
  }
  if (pathname.startsWith('/users/userdocuments/')) {
    const page = t('page_documents', 'Documents');
    return { page, tab: page };
  }
  const item = MENU_ITEMS.find((i) => i.url === pathname) || MENU_ITEMS.find((i) => i.key !== 'home' && itemMatches(i, pathname));
  if (item) {
    const page = labelOf(t, item);
    return { page, tab: page };
  }
  return { page: '', tab: '' };
}

const initialsOf = (profile) => {
  const parts = [profile && profile.firstName, profile && profile.lastName]
    .filter((p) => typeof p === 'string' && p.trim())
    .map((p) => Array.from(p.trim())[0]);
  if (parts.length) return parts.join('').toUpperCase();
  const fallback = (profile && (profile.email || profile.mobile)) || '';
  return fallback ? Array.from(fallback)[0].toUpperCase() : '?';
};

const readOpenGroups = (uid) => {
  try {
    const raw = JSON.parse(localStorage.getItem(`dd_nav_groups_${uid}`) || 'null');
    return raw && typeof raw === 'object' ? raw : {};
  } catch (e) {
    return {};
  }
};

const writeOpenGroups = (uid, value) => {
  try {
    localStorage.setItem(`dd_nav_groups_${uid}`, JSON.stringify(value));
  } catch (e) {
    // storage blocked: the menu just forgets
  }
};

// Desktop only: staff can hide the side menu to give the page the full width.
const readNavHidden = (uid) => {
  try {
    return localStorage.getItem(`dd_nav_hidden_${uid}`) === '1';
  } catch (e) {
    return false;
  }
};

const writeNavHidden = (uid, hidden) => {
  try {
    localStorage.setItem(`dd_nav_hidden_${uid}`, hidden ? '1' : '0');
  } catch (e) {
    // storage blocked: the menu shows again on the next load
  }
};

function CountBadge({ value, label }) {
  if (!value) return null;
  return (
    <Box
      component="span"
      sx={{
        ml: 'auto',
        minWidth: 22,
        height: 22,
        px: '6px',
        boxSizing: 'border-box',
        borderRadius: '11px',
        bgcolor: c.DANGER_TEXT,
        color: c.WHITE,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <span aria-hidden="true">{value > 99 ? '99+' : value}</span>
      <Box component="span" sx={visuallyHidden}>{`${value} ${label}`}</Box>
    </Box>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

// One sidebar row: an icon on a tinted circle and the page name; the page
// you are on is tinted orange with a bar on its leading edge.
function NavRow({ item, label, active, count, countLabel, nested, onNavigate }) {
  const hue = iconHues[item.hue] || iconHues.neutral;
  const Icon = item.Icon;
  const circle = nested ? 24 : 28;
  return (
    <ButtonBase
      component={Link}
      to={item.url}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 1.25,
        minHeight: nested ? 40 : 44,
        py: 0.5,
        pl: nested ? 2 : 1.25,
        pr: 1.25,
        borderRadius: '10px',
        color: active ? c.ACCENT_TEXT : c.INK,
        bgcolor: active ? c.ACCENT_TINT : 'transparent',
        boxShadow: active ? `inset 3px 0 0 ${c.ACCENT}` : 'none',
        fontSize: 14,
        lineHeight: 1.35,
        fontWeight: active ? 700 : nested ? 500 : 600,
        '@media (hover: hover)': { '&:hover': { bgcolor: active ? c.ACCENT_TINT : c.SURFACE_ALT } },
        '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT_TEXT}`, outlineOffset: -2 },
        '@media (pointer: coarse)': { minHeight: 44 },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: circle,
          height: circle,
          borderRadius: '50%',
          bgcolor: active ? c.WHITE : hue.bg,
          color: hue.fg,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <Icon sx={{ fontSize: nested ? 15 : 17 }} />
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        {label}
      </Box>
      <CountBadge value={count} label={countLabel} />
    </ButtonBase>
  );
}

const sectionLabelSx = {
  px: 1.25,
  pt: 1.5,
  pb: 0.75,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.35,
  color: c.TEXT_3,
};

export default function ResponsiveDrawer(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const { signOff, fetchUsersOnce } = api;

  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const withdrawdata = useSelector((state) => state.withdrawdata);
  const sosdata = useSelector((state) => state.sosdata);
  const complaindata = useSelector((state) => state.complaindata);
  const bookings = useSelector((state) => (state.bookinglistdata ? state.bookinglistdata.bookings : null));

  const profile = auth.profile;
  const role = profile && profile.usertype ? profile.usertype : null;
  const uid = profile && profile.uid ? profile.uid : 'anon';
  const appName = (settings && settings.appName) || 'Donkey Drive';

  const switcher = useLanguageSwitcher({ applyProfileLanguage: true });
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(() => readNavHidden(uid));
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [bellAnchor, setBellAnchor] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => readOpenGroups(uid));
  const searchRowRef = useRef(null);

  // Roles that search people also need the light users list (cached, read once).
  useEffect(() => {
    if (role && SEARCH_ROLES.includes(role)) {
      dispatch(fetchUsersOnce());
    }
  }, [role, dispatch, fetchUsersOnce]);

  useEffect(() => {
    setOpenGroups(readOpenGroups(uid));
    setNavHidden(readNavHidden(uid));
  }, [uid]);

  // One menu button: on a desktop it hides or shows the side menu, below md it opens the drawer.
  const desktopNavHidden = isMdUp && navHidden;
  const onMenuButton = () => {
    if (isMdUp) {
      const next = !navHidden;
      setNavHidden(next);
      writeNavHidden(uid, next);
    } else {
      setMobileOpen(true);
    }
  };

  // Counts for the bell and the badges (already-loaded slices, no reads).
  // A withdrawal waits while it is not processed, not reversed and not
  // rejected (a rejected one keeps processed:false with status 'rejected').
  const withdrawsCount = useMemo(() => {
    if (!role || !WITHDRAW_ROLES.includes(role) || !withdrawdata || !Array.isArray(withdrawdata.withdraws)) return 0;
    return withdrawdata.withdraws.filter((w) => !w.processed && !w.reversed && w.status !== 'rejected').length;
  }, [withdrawdata, role]);
  const sosCount = useMemo(() => {
    if (!role || !SOS_ROLES.includes(role) || !sosdata || !Array.isArray(sosdata.sos)) return 0;
    return sosdata.sos.filter((s) => !s.processed).length;
  }, [sosdata, role]);
  const complainsCount = useMemo(() => {
    if (!role || !COMPLAINT_ROLES.includes(role) || !complaindata || !Array.isArray(complaindata.list)) return 0;
    return complaindata.list.filter((x) => !x.check).length;
  }, [complaindata, role]);
  const totalNotifications = withdrawsCount + sosCount + complainsCount;

  // The bell rings once (300 ms) when the count goes up, never all shift.
  const [ring, setRing] = useState(0);
  const prevTotalRef = useRef(totalNotifications);
  useEffect(() => {
    if (totalNotifications > prevTotalRef.current) setRing((n) => n + 1);
    prevTotalRef.current = totalNotifications;
  }, [totalNotifications]);

  const countFor = (key) => {
    if (key === 'withdraws') return withdrawsCount;
    if (key === 'sos') return sosCount;
    if (key === 'complain') return complainsCount;
    return 0;
  };

  // The pages this person can open.
  const visibleItems = useMemo(
    () =>
      MENU_ITEMS.filter(
        (item) =>
          role &&
          (item.access.includes(role) || (item.capability && profile && profile[item.capability] === true)) &&
          (!item.setting || (settings && settings[item.setting] === true))
      ),
    [role, profile, settings]
  );

  const pinSet = role ? PIN_SETS[role] : null;
  const pinnedItems = useMemo(() => {
    if (!pinSet) return [];
    return pinSet.keys
      .map((key) => {
        if (key === 'drivers') return visibleItems.some((i) => i.key === DRIVERS_PIN.requires) ? DRIVERS_PIN : null;
        return visibleItems.find((i) => i.key === key) || null;
      })
      .filter(Boolean);
  }, [pinSet, visibleItems]);

  const accountItems = visibleItems.filter((i) => i.group === 'account');
  const accountAsGroup = accountItems.length > 1;
  const groups = MENU_GROUPS.map((g) => ({
    ...g,
    items: visibleItems.filter((i) => i.group === g.key && (g.key !== 'account' || accountAsGroup)),
  })).filter((g) => g.items.length > 0);

  const isActive = (item) => itemMatches(item, pathname);
  const pinnedActive = pinnedItems.some(isActive);
  const activeGroupKey = (groups.find((g) => g.items.some(isActive)) || {}).key || null;

  // Open the group of the page you are on unless a pin already shows it.
  useEffect(() => {
    if (activeGroupKey && !pinnedActive) {
      setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
    }
  }, [activeGroupKey, pinnedActive]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeOpenGroups(uid, next);
      return next;
    });
  };

  // Close the phone drawer and the search row when the page changes.
  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!searchOpen || !searchRowRef.current) return;
    const input = searchRowRef.current.querySelector('input');
    if (input) input.focus();
  }, [searchOpen]);

  // Page name in the bar and in the browser tab ("SOS · Donkey Drive").
  const titles = resolvePageTitles(pathname, t, bookings);
  const tabTitle = titles.tab ? `${titles.tab} · ${appName}` : appName;
  useEffect(() => {
    // After the current commit, so AuthLoading's appName title does not win.
    const timer = setTimeout(() => {
      document.title = tabTitle;
    }, 0);
    return () => clearTimeout(timer);
  }, [tabTitle, settings]);

  const displayName =
    [profile && profile.firstName, profile && profile.lastName].filter((p) => typeof p === 'string' && p.trim()).join(' ') ||
    (profile && (profile.email || profile.mobile)) ||
    t('profile', 'Profile');
  const roleLabel = role ? t(role, ROLE_NAMES[role] || role) : '';
  const avatarSrc = (profile && (profile.profile_image || profile.profilePicture)) || undefined;
  const canSearch = role && SEARCH_ROLES.includes(role);
  const showBell = role && BELL_ROLES.includes(role);
  const pendingLabel = t('pending_items', 'pending items');
  // settings.version is the MOBILE app's version (it drives forced app
  // updates), so it is not shown here; a build can name the portal's own
  // version with REACT_APP_PORTAL_VERSION.
  const version = process.env.REACT_APP_PORTAL_VERSION ? String(process.env.REACT_APP_PORTAL_VERSION) : '';

  const closeMenus = () => {
    setAccountAnchor(null);
    setBellAnchor(null);
  };

  const handleLogout = () => {
    closeMenus();
    setMobileOpen(false);
    // The lists' kept filters belong to this person only.
    resetPageStates();
    dispatch(signOff());
  };

  const renderNav = (inDrawer) => {
    const onNavigate = inDrawer ? () => setMobileOpen(false) : undefined;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 0 auto', px: 1, pt: 0.5, pb: 1, boxSizing: 'border-box' }}>
        {inDrawer ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1.5, mb: 0.5, borderBottom: `1px solid ${c.OUTLINE}` }}>
            <Avatar src={avatarSrc} alt="" sx={{ width: 40, height: 40, bgcolor: c.ACCENT_TINT, color: c.ACCENT_TEXT, fontSize: 15, fontWeight: 700 }}>
              {initialsOf(profile)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35, color: c.INK, overflowWrap: 'anywhere' }}>{displayName}</Typography>
              {roleLabel ? <Typography sx={{ fontSize: 13, lineHeight: 1.35, color: c.INK_2 }}>{roleLabel}</Typography> : null}
            </Box>
          </Box>
        ) : null}

        {pinnedItems.length > 0 ? (
          <>
            <Box sx={sectionLabelSx}>{t(pinSet.label, pinSet.fallback)}</Box>
            {pinnedItems.map((item) => (
              <NavRow
                key={`pin-${item.key}`}
                item={item}
                label={labelOf(t, item)}
                active={isActive(item)}
                count={countFor(item.key)}
                countLabel={pendingLabel}
                onNavigate={onNavigate}
              />
            ))}
            <Box sx={{ ...sectionLabelSx, pt: 2 }}>{t('nav_all_pages', 'All pages')}</Box>
          </>
        ) : null}

        {groups.map((group) => {
          const open = Boolean(openGroups[group.key]);
          const hasCurrent = group.key === activeGroupKey;
          const panelId = `nav-group-${group.key}${inDrawer ? '-m' : ''}`;
          return (
            <Box key={group.key}>
              <ButtonBase
                onClick={() => toggleGroup(group.key)}
                aria-expanded={open ? 'true' : 'false'}
                aria-controls={panelId}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: 1,
                  minHeight: 40,
                  px: 1.25,
                  borderRadius: '10px',
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: c.INK,
                  '@media (hover: hover)': { '&:hover': { bgcolor: c.SURFACE_ALT } },
                  '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT_TEXT}`, outlineOffset: -2 },
                  '@media (pointer: coarse)': { minHeight: 44 },
                }}
              >
                <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  {t(group.label, group.fallback)}
                </Box>
                {hasCurrent && !open ? (
                  <Box
                    component="span"
                    title={t('nav_group_has_current', 'The page you are on is in here')}
                    sx={{ width: 8, height: 8, borderRadius: '4px', bgcolor: c.ACCENT, flex: 'none' }}
                  />
                ) : null}
                <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.75, flex: 'none' }}>
                  <Box
                    component="span"
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: '6px',
                      boxSizing: 'border-box',
                      borderRadius: '10px',
                      bgcolor: c.SURFACE_ALT,
                      color: c.INK_2,
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {group.items.length}
                  </Box>
                  {open ? (
                    <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: c.INK_2 }} aria-hidden="true" />
                  ) : (
                    <ChevronRightRoundedIcon sx={{ fontSize: 18, color: c.INK_2 }} aria-hidden="true" />
                  )}
                </Box>
              </ButtonBase>
              <Collapse in={open} timeout={180} id={panelId}>
                {group.items.map((item) => (
                  <NavRow
                    key={`${group.key}-${item.key}`}
                    item={item}
                    label={labelOf(t, item)}
                    active={isActive(item)}
                    count={countFor(item.key)}
                    countLabel={pendingLabel}
                    nested
                    onNavigate={onNavigate}
                  />
                ))}
              </Collapse>
            </Box>
          );
        })}

        <Box sx={{ mt: 'auto', pt: 0.75, borderTop: `1px solid ${c.OUTLINE}`, display: 'flex', flexDirection: 'column' }}>
          {!accountAsGroup && accountItems.length === 1 ? (
            <NavRow
              item={accountItems[0]}
              label={labelOf(t, accountItems[0])}
              active={isActive(accountItems[0])}
              count={0}
              nested
              onNavigate={onNavigate}
            />
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ButtonBase
              onClick={handleLogout}
              sx={{
                flex: 1,
                justifyContent: 'flex-start',
                gap: 1.25,
                minHeight: 40,
                px: 1.25,
                borderRadius: '10px',
                color: c.DANGER_TEXT,
                fontSize: 14,
                fontWeight: 600,
                '@media (hover: hover)': { '&:hover': { bgcolor: c.DANGER_TINT } },
                '&.Mui-focusVisible': { outline: `2px solid ${c.DANGER_TEXT}`, outlineOffset: -2 },
                '@media (pointer: coarse)': { minHeight: 44 },
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c.DANGER_TINT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <LogoutRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
              {t(LOG_OUT_KEYS, 'Log out')}
            </ButtonBase>
            {version ? (
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: c.TEXT_3, pr: 1, whiteSpace: 'nowrap' }}>
                {t('portal_version', { version, defaultValue: 'Portal {{version}}' })}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  };

  const barButtonSx = {
    color: c.WHITE,
    width: 40,
    height: 40,
    '@media (hover: hover)': { '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } },
    '&.Mui-focusVisible': { outline: `2px solid ${c.WHITE}`, outlineOffset: 1 },
  };

  // The phone search row (below md) adds a second bar's height.
  const contentTop = BAR + (searchOpen && !isMdUp ? BAR : 0);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        bgcolor: 'background.default',
      }}
    >
      <CssBaseline />
      <GlobalStyles
        styles={{
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-thumb': { backgroundColor: '#C9CED6', borderRadius: 6, border: '2px solid transparent', backgroundClip: 'padding-box' },
          '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9AA1AB' },
          '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '@keyframes dd-bell-ring': {
            '0%': { transform: 'rotate(0deg)' },
            '30%': { transform: 'rotate(-14deg)' },
            '65%': { transform: 'rotate(10deg)' },
            '100%': { transform: 'rotate(0deg)' },
          },
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: c.ACCENT,
          color: c.WHITE,
          border: 0,
          borderRadius: 0,
          boxShadow: 'none',
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
        }}
      >
        <Toolbar
          variant="dense"
          disableGutters
          sx={{ minHeight: BAR, height: BAR, pl: { xs: 0.5, sm: 1, md: 2 }, pr: { xs: 1, md: 2 }, gap: { xs: 0.5, sm: 1 } }}
        >
          <IconButton
            aria-label={
              isMdUp
                ? (navHidden ? t('nav_show_menu', 'Show menu') : t('nav_hide_menu', 'Hide menu'))
                : t('nav_open_menu', 'Open menu')
            }
            title={isMdUp ? (navHidden ? t('nav_show_menu', 'Show menu') : t('nav_hide_menu', 'Hide menu')) : undefined}
            aria-expanded={(isMdUp ? !navHidden : mobileOpen) ? 'true' : 'false'}
            onClick={onMenuButton}
            sx={{ ...barButtonSx, width: 44, height: 44, flex: 'none', ...(isMdUp ? { bgcolor: 'rgba(255,255,255,0.14)' } : {}) }}
          >
            {/* Desktop: a clear arrow (owner, 28 Oct) - "<" hides the side menu,
                ">" brings it back. Phone: the menu icon opens it over the page. */}
            {isMdUp
              ? (navHidden ? <ChevronRightRoundedIcon sx={{ fontSize: 30 }} /> : <ChevronLeftRoundedIcon sx={{ fontSize: 30 }} />)
              : <MenuRoundedIcon />}
          </IconButton>

          <Box
            component={Link}
            to="/"
            aria-label={`${appName}, ${t('home', 'Home')}`}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1.25,
              flex: 'none',
              color: c.WHITE,
              textDecoration: 'none',
              borderRadius: '10px',
              '&:focus-visible': { outline: `2px solid ${c.WHITE}`, outlineOffset: 2 },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: c.WHITE,
                border: '1px solid rgba(255,255,255,0.9)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                overflow: 'hidden',
              }}
            >
              <img src={brandLogo} alt="" width={44} height={44} style={{ objectFit: 'contain', display: 'block', borderRadius: '50%' }} />
            </Box>
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, fontSize: 19, fontWeight: 700, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {appName}
            </Box>
          </Box>

          <Typography
            component="h1"
            noWrap
            sx={{ flex: 1, minWidth: 0, ml: { xs: 0.5, md: 2.5 }, fontSize: 20, fontWeight: 700, lineHeight: 1.35, color: c.WHITE }}
          >
            {titles.page}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.25 }, flex: 'none' }}>
            {canSearch ? (
              <>
                <Box sx={{ display: { xs: 'none', md: 'block' }, width: { md: 240, lg: 290 } }}>
                  <Box sx={barSearchSx}>
                    <QuickSearch />
                  </Box>
                </Box>
                <IconButton
                  aria-label={t('search', 'Search')}
                  aria-expanded={searchOpen ? 'true' : 'false'}
                  onClick={() => setSearchOpen((v) => !v)}
                  sx={{ ...barButtonSx, display: { md: 'none' }, width: 44, height: 44 }}
                >
                  {searchOpen ? <CloseRoundedIcon /> : <SearchRoundedIcon />}
                </IconButton>
              </>
            ) : null}

            {showBell ? (
              <IconButton
                aria-label={t('notifications_waiting', { count: totalNotifications, defaultValue: 'Notifications, {{count}} waiting' })}
                aria-haspopup="menu"
                onClick={(e) => setBellAnchor(e.currentTarget)}
                sx={{
                  ...barButtonSx,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  position: 'relative',
                  '@media (pointer: coarse)': { width: 44, height: 44 },
                }}
              >
                <NotificationsNoneRoundedIcon
                  key={`bell-${ring}`}
                  sx={{ fontSize: 22, animation: ring ? 'dd-bell-ring 300ms ease-out 1' : 'none', transformOrigin: '50% 10%' }}
                />
                {totalNotifications > 0 ? (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      top: -3,
                      right: -4,
                      minWidth: 22,
                      height: 22,
                      px: '6px',
                      boxSizing: 'border-box',
                      borderRadius: '11px',
                      bgcolor: c.DANGER_TEXT,
                      boxShadow: `0 0 0 2px ${c.ACCENT}`,
                      color: c.WHITE,
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {totalNotifications > 99 ? '99+' : totalNotifications}
                  </Box>
                ) : null}
              </IconButton>
            ) : null}

            <ButtonBase
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-label={roleLabel ? `${displayName}, ${roleLabel}` : displayName}
              sx={{
                height: 40,
                pl: 0.5,
                pr: { xs: 0.5, lg: 1.25 },
                gap: 1,
                borderRadius: '20px',
                bgcolor: c.WHITE,
                color: c.INK,
                textAlign: 'left',
                maxWidth: { lg: 240 },
                '&.Mui-focusVisible': { boxShadow: '0 0 0 3px rgba(255,255,255,0.75)' },
                '@media (pointer: coarse)': { height: 44 },
              }}
            >
              <Avatar src={avatarSrc} alt="" sx={{ width: 32, height: 32, bgcolor: c.ACCENT_TINT, color: c.ACCENT_TEXT, fontSize: 13, fontWeight: 700 }}>
                {initialsOf(profile)}
              </Avatar>
              <Box component="span" sx={{ display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
                <Box component="span" sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayName}
                </Box>
                {roleLabel ? (
                  <Box component="span" sx={{ fontSize: 12, color: c.INK_2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {roleLabel}
                  </Box>
                ) : null}
              </Box>
              <KeyboardArrowDownRoundedIcon sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: c.INK_2 }} aria-hidden="true" />
            </ButtonBase>
          </Box>
        </Toolbar>

        {canSearch && searchOpen ? (
          <Box ref={searchRowRef} sx={{ display: { md: 'none' }, height: BAR, px: 1, pb: 1, boxSizing: 'border-box', alignItems: 'center' }}>
            <Box sx={barSearchSx}>
              <QuickSearch />
            </Box>
          </Box>
        ) : null}
      </AppBar>

      <Box component="nav" aria-label={t('nav_portal_pages', 'Portal pages')} sx={{ width: { md: desktopNavHidden ? 0 : SIDEBAR }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 288, maxWidth: '85vw', boxSizing: 'border-box', border: 0, borderRadius: 0, pl: 'env(safe-area-inset-left)' },
          }}
        >
          {renderNav(true)}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: desktopNavHidden ? 'none' : 'block' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR,
              boxSizing: 'border-box',
              top: BAR,
              height: `calc(100% - ${BAR}px)`,
              border: 0,
              borderRight: `1px solid ${c.OUTLINE}`,
              borderRadius: 0,
            },
          }}
        >
          {renderNav(false)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pt: `${contentTop}px`,
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
          bgcolor: 'background.default',
          '--dd-loading-min-height': `calc(100vh - ${contentTop + 48}px)`,
          '@supports (min-height: 100dvh)': { '--dd-loading-min-height': `calc(100dvh - ${contentTop + 48}px)` },
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <OutageBanner />
          <AppUpdateNotice />
          {props.children}
        </Box>
      </Box>

      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={closeMenus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, maxWidth: 'calc(100vw - 24px)', mt: 1, borderRadius: '12px' } }}
      >
        <MenuHeading sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25 }}>
          <Avatar src={avatarSrc} alt="" sx={{ width: 40, height: 40, bgcolor: c.ACCENT_TINT, color: c.ACCENT_TEXT, fontSize: 15, fontWeight: 700 }}>
            {initialsOf(profile)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{displayName}</Typography>
            {roleLabel ? <Typography sx={{ fontSize: 13, lineHeight: 1.35, color: c.INK_2 }}>{roleLabel}</Typography> : null}
            {profile && profile.email ? (
              <Typography sx={{ fontSize: 13, lineHeight: 1.35, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{profile.email}</Typography>
            ) : null}
          </Box>
        </MenuHeading>
        <Divider component="li" />
        {accountItems.map((item) => (
          <MenuItem
            key={`acct-${item.key}`}
            component={Link}
            to={item.url}
            onClick={closeMenus}
            selected={isActive(item)}
            sx={{ minHeight: 44, gap: 1.5 }}
          >
            <item.Icon sx={{ fontSize: 20, color: c.INK_2 }} aria-hidden="true" />
            {labelOf(t, item)}
          </MenuItem>
        ))}
        {/* The language choice lives here, in the profile menu, on every screen size (owner, 28 Oct). */}
        {switcher.languages.length > 1 ? <Divider component="li" /> : null}
        {switcher.languages.length > 1 ? (
          <MenuHeading sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 13, fontWeight: 600, color: c.TEXT_3 }}>
            {t('lang_menu_title', 'Portal language')}
          </MenuHeading>
        ) : null}
        {switcher.languages.length > 1 ? renderLanguageItems({ switcher, onPicked: closeMenus, dense: true }) : null}
        <Divider component="li" />
        <MenuItem onClick={handleLogout} sx={{ minHeight: 44, gap: 1.5, color: c.DANGER_TEXT }}>
          <LogoutRoundedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
          {t(LOG_OUT_KEYS, 'Log out')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={bellAnchor}
        open={Boolean(bellAnchor)}
        onClose={closeMenus}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxWidth: 'calc(100vw - 24px)', maxHeight: 440, mt: 1, borderRadius: '12px' } }}
      >
        <MenuHeading sx={{ px: 2, pt: 1.25, pb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{t('notifications', 'Notifications')}</Typography>
          {totalNotifications > 0 ? (
            <Typography sx={{ fontSize: 13, color: c.INK_2 }}>{`${totalNotifications} ${pendingLabel}`}</Typography>
          ) : null}
        </MenuHeading>
        <Divider component="li" />
        {totalNotifications === 0 ? (
          <MenuHeading sx={{ px: 2, py: 3, textAlign: 'center', fontSize: 14, color: c.INK_2 }}>
            {t('no_new_notifications', 'No new notifications')}
          </MenuHeading>
        ) : null}
        {[
          { key: 'withdraws', url: '/withdraws', count: withdrawsCount, title: t('withdraws_web', 'Withdraws'), line: t('pending_withdrawals', 'pending withdrawals'), Icon: PaymentsOutlinedIcon, hue: iconHues.amber },
          { key: 'sos', url: '/sos', count: sosCount, title: t('sos', 'SOS'), line: t('pending_sos', 'pending SOS requests'), Icon: WarningAmberRoundedIcon, hue: iconHues.danger },
          { key: 'complain', url: '/complain', count: complainsCount, title: t(['nav_complaints', 'complain'], 'Complaints'), line: t('pending_complaints', 'pending complaints'), Icon: ChatBubbleOutlineRoundedIcon, hue: iconHues.amber },
        ]
          .filter((row) => row.count > 0)
          .map((row) => (
            <MenuItem key={`bell-${row.key}`} component={Link} to={row.url} onClick={closeMenus} sx={{ minHeight: 56, gap: 1.5, py: 1 }}>
              <Box
                component="span"
                aria-hidden="true"
                sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: row.hue.bg, color: row.hue.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}
              >
                <row.Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{row.title}</Typography>
                <Typography sx={{ fontSize: 13, color: c.INK_2, lineHeight: 1.35, whiteSpace: 'normal' }}>{`${row.count} ${row.line}`}</Typography>
              </Box>
              <CountBadge value={row.count} label={pendingLabel} />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}

// QuickSearch restyled as the board's white search pill on the orange bar.
const barSearchSx = {
  width: '100%',
  // QuickSearch's own root is 300 px wide on md+; let it fill the pill's slot instead.
  '& > div': { width: '100%', mx: 0 },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
    backgroundColor: c.WHITE,
    height: 40,
    borderRadius: '10px',
    boxShadow: 'none',
    '@media (pointer: coarse)': { height: 44 },
  },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root:hover': { backgroundColor: c.WHITE },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 3px rgba(255,255,255,0.45)' },
  '& .MuiAutocomplete-root .MuiInputBase-input': { color: c.INK, fontSize: 14, '@media (pointer: coarse)': { fontSize: 16 } },
  '& .MuiAutocomplete-root .MuiInputBase-input::placeholder': { color: c.TEXT_3, opacity: 1 },
  '& .MuiAutocomplete-root .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.INK_2 },
  '& .MuiAutocomplete-root .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: c.INK_2 },
  '& .MuiAutocomplete-root .MuiCircularProgress-root': { color: c.ACCENT },
};
